import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type {
  ParceiroAutenticado,
  Cupom,
  Comissao,
  AssinaturaPgArt,
  SolicitacaoAssistencia,
} from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgTableBadgeCell } from '@pgcustom/design-system';
import { logger } from '../../core/logger.js';
import type { Resultado } from '../../core/resultado.js';
import type { PortalParceiroApi } from './portal-parceiro.api.js';
import './solicitar-assistencia-modal.js';
import type { ErpPortalSolicitarAssistenciaModal } from './solicitar-assistencia-modal.js';

const STATUS_COMISSAO_BADGE: Record<Comissao['status'], PgTableBadgeCell> = {
  pendente: { texto: 'Pendente', tom: 'alerta' },
  paga: { texto: 'Paga', tom: 'sucesso' },
  estornada: { texto: 'Estornada', tom: 'erro' },
};

const STATUS_ASSINATURA_BADGE: Record<AssinaturaPgArt['status'], PgTableBadgeCell> = {
  pendente: { texto: 'Aguardando pagamento', tom: 'alerta' },
  confirmada: { texto: 'Confirmada', tom: 'sucesso' },
  cancelada: { texto: 'Cancelada', tom: 'erro' },
};

const STATUS_ASSISTENCIA_BADGE: Record<SolicitacaoAssistencia['status'], PgTableBadgeCell> = {
  pendente: { texto: 'Pendente', tom: 'neutro' },
  aprovada: { texto: 'Aprovada', tom: 'destaque' },
  enviado: { texto: 'Enviado', tom: 'destaque' },
  recebido: { texto: 'Recebido pela assistência', tom: 'destaque' },
  em_analise: { texto: 'Em análise', tom: 'alerta' },
  em_reparo: { texto: 'Em reparo', tom: 'alerta' },
  concluida: { texto: 'Concluída', tom: 'sucesso' },
  devolvida: { texto: 'Devolvida', tom: 'neutro' },
};

/**
 * `<erp-portal-parceiro-page>` — painel do parceiro autenticado (Decisão
 * do owner: mesma estrutura para Sena/Bichão/Savytzz/Renato Rosk,
 * `DECISIONS.md` DEC-037/análise §12; referência visual "fase de
 * finalização", perfil Parceiro). Mostra só "meus" dados — nunca um id
 * de parceiro recebido de fora, nunca dado de outro parceiro, nunca
 * acesso a módulos administrativos do ERP interno (a API por trás,
 * `/portal-parceiros/me/*`, já resolve ownership pelo cookie de sessão).
 * Layout próprio, deliberadamente sem `<erp-shell>`/sidebar do ERP
 * interno.
 *
 * Cliques/indicações/link de afiliado (presentes na referência visual)
 * NÃO têm nenhuma origem de dado real hoje — nenhum sistema de rastreio
 * de link/clique existe no domínio. Mostrados como estado explícito
 * "não implementado", nunca um número inventado.
 */
export class ErpPortalParceiroPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _portalApi: PortalParceiroApi | null = null;
  private _parceiro: ParceiroAutenticado | null = null;
  private _aoSair: (() => void) | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set portalApi(valor: PortalParceiroApi) {
    this._portalApi = valor;
  }

  set parceiro(valor: ParceiroAutenticado) {
    this._parceiro = valor;
  }

  set aoSair(valor: () => void) {
    this._aoSair = valor;
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    void this.carregarComissoes();
    void this.carregarCupons();
    void this.carregarAssinaturas();
    void this.carregarAssistencias();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host {
          display: block;
          min-height: 100vh;
          background: var(--pg-color-bg-base, #121417);
          font-family: var(--pg-font-familia-base, system-ui);
        }
        header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--pg-space-4, 16px) var(--pg-space-6, 32px);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
        }
        .marca {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-lg, 1.125rem);
          font-weight: var(--pg-font-weight-bold, 700);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .marca span { color: var(--pg-color-roxo, #8b5cf6); }
        .subtitulo-marca {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        .conta {
          display: flex;
          align-items: center;
          gap: var(--pg-space-4, 16px);
        }
        .conta-nome {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .sair {
          background: none;
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          font-size: var(--pg-font-size-xs, 0.75rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .sair:hover { color: var(--pg-color-texto-primario, #f2f3f5); border-color: var(--pg-color-texto-secundario, #9aa1ab); }
        main { padding: var(--pg-space-6, 32px); display: flex; flex-direction: column; gap: var(--pg-space-5, 24px); }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0;
        }
        .sub { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-sm, 0.875rem); margin: var(--pg-space-1, 4px) 0 0; }
        .kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: var(--pg-space-4, 16px);
        }
        .secoes {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: var(--pg-space-4, 16px);
        }
        .secao-header-acao {
          display: flex;
          justify-content: flex-end;
        }
        .link-acao {
          background: none;
          border: none;
          color: var(--pg-color-roxo, #8b5cf6);
          font-size: var(--pg-font-size-xs, 0.75rem);
          cursor: pointer;
          padding: 0;
        }
        .link-acao:hover { color: var(--pg-color-roxo-hover, #a78bfa); }
      </style>
      <header>
        <div>
          <div class="marca">PG <span>Custom</span></div>
          <div class="subtitulo-marca">Portal do Parceiro</div>
        </div>
        <div class="conta">
          <span class="conta-nome">${escaparHtml(this._parceiro?.nome ?? '')}</span>
          <button class="sair" type="button" id="botao-sair">Sair</button>
        </div>
      </header>
      <main>
        <div>
          <h1>Olá, ${escaparHtml(primeiroNome(this._parceiro?.nome ?? ''))}!</h1>
          <p class="sub">Acompanhe suas comissões, cupons, assinaturas e assistências técnicas.</p>
        </div>

        <div class="kpis">
          <pg-kpi-card id="kpi-comissao-pendente" label="Comissão pendente" icone="dinheiro" estado="carregando"></pg-kpi-card>
          <pg-kpi-card id="kpi-comissao-paga" label="Comissão paga" icone="dinheiro" estado="carregando"></pg-kpi-card>
          <pg-kpi-card id="kpi-vendas" label="Vendas (via cupom)" icone="carrinho" estado="carregando"></pg-kpi-card>
          <pg-kpi-card id="kpi-assinaturas" label="Assinaturas confirmadas" icone="parceiros" estado="carregando"></pg-kpi-card>
        </div>

        <div class="secoes">
          <pg-card>
            <span slot="header">Minhas comissões</span>
            <div id="secao-comissoes"><pg-empty-state variant="carregando" titulo="Minhas comissões"></pg-empty-state></div>
          </pg-card>
          <pg-card>
            <span slot="header">Meus cupons</span>
            <div id="secao-cupons"><pg-empty-state variant="carregando" titulo="Meus cupons"></pg-empty-state></div>
          </pg-card>
          <pg-card>
            <span slot="header">Minhas assinaturas PG Art</span>
            <div id="secao-assinaturas"><pg-empty-state variant="carregando" titulo="Minhas assinaturas"></pg-empty-state></div>
          </pg-card>
          <pg-card>
            <span slot="header">Assistência técnica</span>
            <span slot="header-acao"><button class="link-acao" id="botao-nova-assistencia" type="button">+ Nova solicitação</button></span>
            <div id="secao-assistencias"><pg-empty-state variant="carregando" titulo="Assistência técnica"></pg-empty-state></div>
          </pg-card>
          <pg-card>
            <span slot="header">Indicações e cliques</span>
            <pg-empty-state
              variant="indisponivel"
              titulo="Ainda não disponível"
              descricao="O rastreio de cliques e indicações do seu link ainda não existe no sistema. Assim que for implementado, aparece aqui."
            ></pg-empty-state>
          </pg-card>
        </div>
      </main>
      <erp-portal-solicitar-assistencia-modal id="modal-nova-assistencia"></erp-portal-solicitar-assistencia-modal>
    `;

    this.root.querySelector('#botao-sair')?.addEventListener('click', () => this._aoSair?.());

    const modal = this.root.querySelector('#modal-nova-assistencia') as ErpPortalSolicitarAssistenciaModal;
    if (this._portalApi) modal.portalApi = this._portalApi;
    modal.aoEnviar = () => {
      void this.carregarAssistencias();
    };
    this.root.querySelector('#botao-nova-assistencia')?.addEventListener('click', () => modal.abrir());
  }

  private async carregarComissoes(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const comissoes = await api.minhasComissoes();

      const pendenteCentavos = comissoes.filter((c) => c.status === 'pendente').reduce((soma, c) => soma + c.valorCentavos, 0);
      const pagaCentavos = comissoes.filter((c) => c.status === 'paga').reduce((soma, c) => soma + c.valorCentavos, 0);
      const vendas = comissoes.filter((c) => c.tipo === 'parceiro' || c.tipo === 'cupom_atribuicao').length;

      this.definirKpi('kpi-comissao-pendente', formatBRL(centavosParaReais(pendenteCentavos)));
      this.definirKpi('kpi-comissao-paga', formatBRL(centavosParaReais(pagaCentavos)));
      this.definirKpi('kpi-vendas', String(vendas));

      const resultado: Resultado<Comissao[]> = comissoes.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: comissoes };
      this.renderSecao('comissoes', resultado, (dado) => {
        const colunas: PgTableColumn[] = [
          { key: 'tipo', label: 'Origem' },
          { key: 'valor', label: 'Valor', align: 'right' },
          { key: 'status', label: 'Status' },
          { key: 'criadoEm', label: 'Gerada em' },
        ];
        const linhas = dado.map((comissao) => ({
          tipo: rotuloTipoComissao(comissao.tipo),
          valor: formatBRL(centavosParaReais(comissao.valorCentavos)),
          status: STATUS_COMISSAO_BADGE[comissao.status],
          criadoEm: new Date(comissao.criadoEm).toLocaleDateString('pt-BR'),
        }));
        return { colunas, linhas };
      }, 'nenhuma comissão gerada ainda');
    } catch (erro) {
      logger.erro('falha ao carregar minhas comissões', { erro });
      this.aplicarEstadoKpi('kpi-comissao-pendente');
      this.aplicarEstadoKpi('kpi-comissao-paga');
      this.aplicarEstadoKpi('kpi-vendas');
      this.renderSecaoEstado('comissoes', 'Minhas comissões', { status: 'erro', mensagem: 'falha ao carregar' }, '');
    }
  }

  private async carregarCupons(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const cupons = await api.meusCupons();
      const resultado: Resultado<Cupom[]> = cupons.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: cupons };
      this.renderSecao('cupons', resultado, (dado) => {
        const colunas: PgTableColumn[] = [
          { key: 'codigo', label: 'Código' },
          { key: 'desconto', label: 'Desconto', align: 'right' },
          { key: 'pix', label: 'Forma exigida' },
          { key: 'status', label: 'Status' },
        ];
        const linhas = dado.map((cupom) => ({
          codigo: cupom.codigo,
          desconto: `${cupom.descontoPercentual}%`,
          pix: cupom.formaPagamentoExigida === 'pix' ? 'PIX' : '—',
          status: cupom.ativo
            ? ({ texto: 'Ativo', tom: 'sucesso' } satisfies PgTableBadgeCell)
            : ({ texto: 'Inativo', tom: 'neutro' } satisfies PgTableBadgeCell),
        }));
        return { colunas, linhas };
      }, 'nenhum cupom cadastrado ainda');
    } catch (erro) {
      logger.erro('falha ao carregar meus cupons', { erro });
      this.renderSecaoEstado('cupons', 'Meus cupons', { status: 'erro', mensagem: 'falha ao carregar' }, '');
    }
  }

  private async carregarAssinaturas(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const assinaturas = await api.minhasAssinaturas();
      const confirmadas = assinaturas.filter((a) => a.status === 'confirmada').length;
      this.definirKpi('kpi-assinaturas', String(confirmadas));

      const resultado: Resultado<AssinaturaPgArt[]> =
        assinaturas.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: assinaturas };
      this.renderSecao('assinaturas', resultado, (dado) => {
        const colunas: PgTableColumn[] = [
          { key: 'valor', label: 'Valor', align: 'right' },
          { key: 'status', label: 'Status' },
          { key: 'criadoEm', label: 'Criada em' },
        ];
        const linhas = dado.map((assinatura) => ({
          valor: formatBRL(centavosParaReais(assinatura.valorCentavos)),
          status: STATUS_ASSINATURA_BADGE[assinatura.status],
          criadoEm: new Date(assinatura.criadoEm).toLocaleDateString('pt-BR'),
        }));
        return { colunas, linhas };
      }, 'nenhuma assinatura vinculada a você ainda');
    } catch (erro) {
      logger.erro('falha ao carregar minhas assinaturas', { erro });
      this.aplicarEstadoKpi('kpi-assinaturas');
      this.renderSecaoEstado('assinaturas', 'Minhas assinaturas', { status: 'erro', mensagem: 'falha ao carregar' }, '');
    }
  }

  private async carregarAssistencias(): Promise<void> {
    const api = this._portalApi;
    if (!api) return;
    try {
      const solicitacoes = await api.minhasAssistencias();
      const resultado: Resultado<SolicitacaoAssistencia[]> =
        solicitacoes.length === 0 ? { status: 'vazio' } : { status: 'ok', dado: solicitacoes };
      this.renderSecao('assistencias', resultado, (dado) => {
        const colunas: PgTableColumn[] = [
          { key: 'codigo', label: 'Código' },
          { key: 'produto', label: 'Produto' },
          { key: 'status', label: 'Status' },
          { key: 'criadoEm', label: 'Solicitada em' },
        ];
        const linhas = dado.map((solicitacao) => ({
          codigo: solicitacao.codigo,
          produto: solicitacao.produtoDescricao,
          status: STATUS_ASSISTENCIA_BADGE[solicitacao.status],
          criadoEm: new Date(solicitacao.criadoEm).toLocaleDateString('pt-BR'),
        }));
        return { colunas, linhas };
      }, 'nenhuma solicitação de assistência ainda');
    } catch (erro) {
      logger.erro('falha ao carregar minhas assistências', { erro });
      this.renderSecaoEstado('assistencias', 'Assistência técnica', { status: 'erro', mensagem: 'falha ao carregar' }, '');
    }
  }

  private definirKpi(id: string, valor: string): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
  }

  private aplicarEstadoKpi(id: string): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'indisponivel');
  }

  private renderSecao<T>(
    id: string,
    resultado: Resultado<T>,
    paraTabela: (dado: T) => { colunas: PgTableColumn[]; linhas: Record<string, string | number | boolean | null | PgTableBadgeCell>[] },
    mensagemVazio: string
  ): void {
    if (resultado.status === 'ok') {
      const body = this.root.querySelector(`#secao-${id}`);
      if (!body) return;
      const { colunas, linhas } = paraTabela(resultado.dado);
      body.innerHTML = '<pg-table></pg-table>';
      const tabela = body.querySelector('pg-table') as PgTable;
      tabela.columns = colunas;
      tabela.rows = linhas;
      return;
    }
    this.renderSecaoEstado(id, tituloDaSecao(id), resultado, mensagemVazio);
  }

  private renderSecaoEstado(id: string, titulo: string, resultado: Resultado<unknown>, mensagemVazio: string): void {
    const body = this.root.querySelector(`#secao-${id}`);
    if (!body) return;
    const variant =
      resultado.status === 'sem-permissao'
        ? 'sem-permissao'
        : resultado.status === 'erro'
          ? 'erro'
          : resultado.status === 'vazio'
            ? 'vazio'
            : 'indisponivel';
    const descricao = resultado.status === 'vazio' ? mensagemVazio : legendaDoResultado(resultado);
    body.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }
}

function tituloDaSecao(id: string): string {
  switch (id) {
    case 'comissoes':
      return 'Minhas comissões';
    case 'cupons':
      return 'Meus cupons';
    case 'assinaturas':
      return 'Minhas assinaturas';
    case 'assistencias':
      return 'Assistência técnica';
    default:
      return '';
  }
}

function rotuloTipoComissao(tipo: Comissao['tipo']): string {
  switch (tipo) {
    case 'parceiro':
      return 'Venda (cupom)';
    case 'cupom_atribuicao':
      return 'Atribuição por cupom';
    case 'assinatura_pg_art':
      return 'Assinatura PG Art';
    case 'colaborador_global':
      return 'Comissão global';
    case 'tecnico':
      return 'Serviço técnico';
    default:
      return tipo;
  }
}

function primeiroNome(nomeCompleto: string): string {
  return nomeCompleto.trim().split(/\s+/)[0] ?? '';
}

function legendaDoResultado(resultado: Resultado<unknown>): string {
  switch (resultado.status) {
    case 'indisponivel':
      return resultado.motivo;
    case 'sem-permissao':
      return 'você não tem permissão para ver este dado';
    case 'erro':
      return resultado.mensagem;
    default:
      return 'módulo ainda não implementado';
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-portal-parceiro-page', ErpPortalParceiroPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-portal-parceiro-page': ErpPortalParceiroPage;
  }
}
